'use client';

import { useEffect, useState } from 'react';
import { compile, run } from '@mdx-js/mdx';
import * as runtime from 'react/jsx-runtime';
import * as runtimeDev from 'react/jsx-dev-runtime';
import { mdxComponents } from './mdx-components';
import { proseClassName } from './proseClassName';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';

interface MDXContentProps {
  content: string; // MDX string from database
}

export function MDXContent({ content }: MDXContentProps) {
  const [MdxContent, setMdxContent] = useState<any>(null);
  const [error, setError] = useState<{
    message: string;
    line?: number;
    column?: number;
    reason?: string;
  } | null>(null);

  useEffect(() => {
    async function compileMDX() {
      try {
        // Clear previous error
        setError(null);

        const isDev = process.env.NODE_ENV === 'development';

        // Compile MDX string to JavaScript
        const compiled = await compile(content, {
          outputFormat: 'function-body',
          development: isDev,
          remarkPlugins: [remarkGfm],
          rehypePlugins: [rehypeHighlight],
        });

        // Run the compiled code with appropriate runtime
        const { default: Content } = await run(String(compiled), {
          ...(isDev ? runtimeDev : runtime),
          baseUrl: import.meta.url,
        });

        setMdxContent(() => Content);
        setError(null);
      } catch (err: any) {
        // Suppress console error to avoid Next.js error overlay
        // Only log in development for debugging
        if (process.env.NODE_ENV === 'development') {
          console.warn('MDX compilation error (handled):', err);
        }

        // Extract detailed error information
        let message = err instanceof Error ? err.message : 'Failed to compile MDX';
        let reason = err?.reason;

        // Try to extract component name from error message
        if (message.includes('Cannot read properties of undefined')) {
          const propertyMatch = message.match(/reading '(\w+)'/);
          if (propertyMatch) {
            reason = `Pokus o přístup k vlastnosti '${propertyMatch[1]}' na undefined objektu. Zkontrolujte, zda jsou všechny komponenty správně importovány a props předány.`;
          }
        }

        const errorInfo = {
          message,
          line: err?.line,
          column: err?.column,
          reason,
        };

        setError(errorInfo);
        setMdxContent(null);
      }
    }

    compileMDX();
  }, [content]);

  if (error) {
    return (
      <div className="rounded-lg border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500 dark:bg-red-600 flex items-center justify-center text-white font-bold">
            ✕
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-red-900 dark:text-red-200 mb-2">
              Chyba v MDX syntaxi
            </h3>

            {/* Error location */}
            {(error.line || error.column) && (
              <div className="mb-3 inline-flex items-center gap-2 px-3 py-1 bg-red-100 dark:bg-red-950/50 rounded text-sm font-mono text-red-800 dark:text-red-300">
                {error.line && <span>Řádek {error.line}</span>}
                {error.column && <span>Sloupec {error.column}</span>}
              </div>
            )}

            {/* Error reason */}
            {error.reason && (
              <div className="mb-3 p-3 bg-red-100 dark:bg-red-950/50 rounded">
                <p className="text-sm font-medium text-red-900 dark:text-red-200 mb-1">
                  Důvod:
                </p>
                <p className="text-sm text-red-800 dark:text-red-300">
                  {error.reason}
                </p>
              </div>
            )}

            {/* Main error message */}
            <div className="mb-3 p-3 bg-white dark:bg-gray-950 rounded border border-red-200 dark:border-red-800">
              <p className="text-sm font-mono text-red-900 dark:text-red-200 whitespace-pre-wrap break-words">
                {error.message}
              </p>
            </div>

            {/* Help text */}
            <p className="text-sm text-red-700 dark:text-red-400">
              💡 Náhled se automaticky aktualizuje, jakmile opravíte chybu v editoru.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!MdxContent) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
      </div>
    );
  }

  return (
    <article className={proseClassName}>
      <MdxContent components={mdxComponents} />
    </article>
  );
}
